import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useFormikContext } from "formik";
import { constants } from "@qgiv/core-js";
import { FormikField } from "@qgiv/core-donor";
import { submitParticipantAction } from "@qgiv/p2p-registration";
import { useDispatch } from "react-redux";
import {
    getIfEmailExistsInCurrentRegistration,
    handleVerificationSuccess,
    handleVerificationError,
} from "../../../redux/utilities/emailVerificationHelpers";
import AccountRegistrationError from "./AccountRegistrationError";

const EmailRegistrationField = ({
    alias,
    emailDescriptionText,
    emailLabel,
    emailOptional,
    emailVerification,
    registrations,
    updateRegistrationAccountValidation,
    currentRegistration = 0,
    updateLastVerifiedEmail,
    updateEmailVerificationInFlight,
    updateEmailHasBeenVerified,
    updateEmailExistsInCurrentRegistration,
    updateEmailExistsInPreviousRegistration,
    updateScrollToEmailField,
    updateYourFundraisingDashboard,
}) => {
    const {
        errors,
        values,
        touched,
        setSubmitting,
        setFieldTouched,
        setFieldError,
    } = useFormikContext();

    const { Email, Is_Nfp, Managed_By_Primary } = values;

    const {
        emailExistsInCurrentRegistration,
        emailExistsInPreviousRegistration,
        lastVerifiedEmail,
        scrollToEmailField,
        yourFundraisingDashboard,
    } = emailVerification;

    const dispatch = useDispatch();

    useEffect(() => {
        if (scrollToEmailField) {
            // immediately set scrollToEmailField to false since
            // when try, it will focus on the email field and we
            // no longer need it to be true.
            dispatch(updateScrollToEmailField(false));
        }
    }, [dispatch, scrollToEmailField, updateScrollToEmailField]);

    // Need to wrap redux actions in dispatch before they are passed to
    // common helper functions
    const dispatchUpdateEmailHasBeenVerified = (_emailHasBeenVerified) => {
        dispatch(updateEmailHasBeenVerified(_emailHasBeenVerified));
    };
    const dispatchUpdateEmailVerificationInFlight = (
        _verificationIsInFlight,
    ) => {
        dispatch(updateEmailVerificationInFlight(_verificationIsInFlight));
    };
    const dispatchUpdateLastVerifiedEmail = (_lastVerifiedEmail) => {
        dispatch(updateLastVerifiedEmail(_lastVerifiedEmail));
    };
    const dispatchUpdateRegistrationAccountValidation = (
        successValidationData,
    ) => {
        dispatch(updateRegistrationAccountValidation(successValidationData));
    };
    const dispatchUpdateEmailExistsInPreviousRegistration = (
        _emailExistsInPreviousRegistration,
    ) =>
        dispatch(
            updateEmailExistsInPreviousRegistration(
                _emailExistsInPreviousRegistration,
            ),
        );

    const dispatchUpdateYourFundraisingDashboard = (
        _yourFundraisingDashboard,
    ) => dispatch(updateYourFundraisingDashboard(_yourFundraisingDashboard));

    const {
        ENUMS: { GlobalAccountAction },
    } = constants;

    const hasEmailValue = Boolean(Email);
    const hasEmailError = Boolean(errors.Email);

    // For whatever reason, when using autofill on iOS in a Chrome Browser the onBlur does not get triggered.
    // This only becomes a problem if a network call is needed on an input and that network call responds to the blur/change/focus event
    // Here we are detecting the device and browser in order to only manually call handleEmailBlur if necessary
    const [isIOS, setIsIOS] = useState(false);
    const [isChrome, setIsChrome] = useState(false);

    useEffect(() => {
        const userAgent =
            typeof window.navigator === "undefined" ? "" : navigator.userAgent;
        const iOSDevice = Boolean(userAgent.match(/iPhone|iPad|iPod/i));
        const chromeBrowser = Boolean(userAgent.match(/CriOS/i));
        setIsIOS(iOSDevice);
        setIsChrome(chromeBrowser);
    }, []);

    const handleEmailVerificationInCurrentRegistration = () => {
        const currentEmailExistsInCurrentRegistration =
            getIfEmailExistsInCurrentRegistration({
                currentRegistration,
                registrations,
                Email,
            });
        dispatch(
            updateEmailExistsInCurrentRegistration(
                currentEmailExistsInCurrentRegistration,
            ),
        );
    };

    const isPrimaryRegistrant = currentRegistration === 0;

    const handleEmailVerificationInPreviousRegistrations = () => {
        const data = {
            Email,
            Global_Account_Action: GlobalAccountAction.SEARCH_FOR_EXISTENCE,
        };
        const participantAction = `/event/${alias}/register/account/participantaction/`;

        // this value disables the next buttons actions
        dispatch(updateEmailVerificationInFlight(true));

        submitParticipantAction(participantAction, data)
            .then((response) =>
                handleVerificationSuccess({
                    dispatchUpdateRegistrationAccountValidation,
                    dispatchUpdateLastVerifiedEmail,
                    dispatchUpdateEmailVerificationInFlight,
                    dispatchUpdateEmailHasBeenVerified,
                    dispatchUpdateEmailExistsInPreviousRegistration,
                    dispatchUpdateYourFundraisingDashboard,
                    Email,
                    response,
                    setFieldError,
                }),
            )
            .catch((error) =>
                handleVerificationError({
                    dispatchUpdateLastVerifiedEmail,
                    dispatchUpdateEmailVerificationInFlight,
                    dispatchUpdateEmailHasBeenVerified,
                    dispatchUpdateRegistrationAccountValidation,
                    dispatchUpdateYourFundraisingDashboard,
                    Email,
                    error,
                    setSubmitting,
                }),
            );
    };

    // This is going to be our first line of defense for Email Validation
    // When blur is detected, we are going to make an API call to get current
    // state of that email value. But our last line of defense is going to take place
    // within the RegistrationPage onSubmit function, which will do the same if the values
    // for emails do not line up or validation is in flight.
    const handleEmailBlur = (e) => {
        e?.persist();
        // If the user triggers a blur on the email field by clicking the
        // next buttons, we want to let the onSubmit handler for RegistrationPage
        // handle validating the current email value during the submit process.
        const elementThatTriggeredBlur = e?.relatedTarget;

        // if the event data is missing or does not have a specific relatedTarget
        // in Chrome on iOS, we want to not run email verification on blur and
        // let the page submission logic handle it.
        if (!elementThatTriggeredBlur && isIOS && isChrome) {
            return;
        }

        // We do not want to let email verification run when the next element
        // interacted with by the user would trigger page submission.
        if (elementThatTriggeredBlur) {
            const { name = "" } = elementThatTriggeredBlur;
            if (
                name === "Save_And_Continue_To_Payment" ||
                name === "Save_And_Add_Another_Registration"
            ) {
                return;
            }
        }

        // Set the touched value of the Email field manually as Formik does
        // not do this as we are passing in our own handleBlur function
        setFieldTouched("Email");

        if (!Is_Nfp && !Managed_By_Primary) {
            // This will evaluate if Email's value exists within the current registration
            // and set the errored values if there are errors.
            handleEmailVerificationInCurrentRegistration();

            const hasChangedSincePreviousValidation =
                Email !== lastVerifiedEmail;

            if (
                hasEmailValue &&
                !hasEmailError &&
                hasChangedSincePreviousValidation
            ) {
                handleEmailVerificationInPreviousRegistrations();
            }
        }
    };

    useEffect(() => {
        //    This needs to only run on iOS Chrome when autofilled
        if (isIOS && isChrome && Email) {
            handleEmailBlur();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Email, touched]);

    const emailHasPassedBasicValidation = !!(hasEmailValue && !hasEmailError);

    return (
        <>
            <FormikField
                label={emailLabel}
                name="Email"
                type="email"
                id="Email"
                helpText={emailDescriptionText}
                handleBlur={handleEmailBlur}
                descText={emailOptional ? "Optional" : ""}
                ariaRequired={emailOptional ? "false" : "true"}
                scrollAndFocus={scrollToEmailField}
                autoComplete="email"
            />
            {/* If the user has entered an email and it passed the basic email validation */}
            {emailHasPassedBasicValidation && (
                <AccountRegistrationError
                    Email={Email}
                    emailExistsInPreviousRegistration={
                        emailExistsInPreviousRegistration
                    }
                    emailExistsInCurrentRegistration={
                        emailExistsInCurrentRegistration
                    }
                    lastVerifiedEmail={lastVerifiedEmail}
                    Is_Nfp={Is_Nfp}
                    isPrimaryRegistrant={isPrimaryRegistrant}
                    Managed_By_Primary={Managed_By_Primary}
                    yourFundraisingDashboard={yourFundraisingDashboard}
                />
            )}
        </>
    );
};

EmailRegistrationField.propTypes = {
    alias: PropTypes.string.isRequired,
    emailDescriptionText: PropTypes.string.isRequired,
    emailLabel: PropTypes.string.isRequired,
    emailOptional: PropTypes.bool.isRequired,
    updateRegistrationAccountValidation: PropTypes.func.isRequired,
    registrations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    currentRegistration: PropTypes.number,
    emailVerification: PropTypes.shape({
        emailExistsInCurrentRegistration: PropTypes.bool,
        emailExistsInPreviousRegistration: PropTypes.bool,
        lastVerifiedEmail: PropTypes.string,
        scrollToEmailField: PropTypes.bool,
        yourFundraisingDashboard: PropTypes.string,
    }).isRequired,
    updateLastVerifiedEmail: PropTypes.func.isRequired,
    updateEmailVerificationInFlight: PropTypes.func.isRequired,
    updateEmailHasBeenVerified: PropTypes.func.isRequired,
    updateEmailExistsInCurrentRegistration: PropTypes.func.isRequired,
    updateEmailExistsInPreviousRegistration: PropTypes.func.isRequired,
    updateScrollToEmailField: PropTypes.func.isRequired,
    updateYourFundraisingDashboard: PropTypes.func.isRequired,
};

export default EmailRegistrationField;
